import { useEffect, useState } from "react";
import api from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import MaintenanceThread from "@/components/MaintenanceThread";
import { Card, Badge, gbp, fmtDate, Loading } from "@/components/ui-bits";
import { Home, Wrench, FileText, Plus, X, CheckCircle2, Clock, Paperclip, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export default function TenantPortal() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "Medium" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [expandedMaint, setExpandedMaint] = useState(null);
  const [replyBusy, setReplyBusy] = useState(false);

  const load = () => api.get("/portal/tenant").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: req } = await api.post("/portal/tenant/maintenance", form);
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "maintenance");
        fd.append("entity_id", req.request_id);
        fd.append("bucket", "photos");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length ? `Maintenance reported with ${files.length} file${files.length > 1 ? "s" : ""} attached` : "Maintenance reported");
      setOpen(false);
      setForm({ title: "", description: "", priority: "Medium" });
      setFiles([]);
      setJustSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      load();
    } catch { toast.error("Could not submit your report"); }
    finally { setBusy(false); }
  };

  const tenantReply = async (rid, text) => {
    setReplyBusy(true);
    try { await api.post(`/portal/tenant/maintenance/${rid}/reply`, { text }); await load(); }
    catch { toast.error("Could not send your reply"); }
    finally { setReplyBusy(false); }
  };

  if (!data) return <PortalLayout title="My Tenancy" icon={Home}><Loading /></PortalLayout>;
  const p = data.property;
  if (!p) return <PortalLayout title="My Tenancy" icon={Home}><p className="text-slate-500">No tenancy is currently linked to your account.</p></PortalLayout>;

  const lastPayment = data.payments[0];
  const statusIcon = { Reported: Clock, "In Progress": Wrench, Resolved: CheckCircle2 };

  return (
    <PortalLayout title="My Tenancy" subtitle="Tenant" icon={Home}>
      {justSubmitted && (
        <div data-testid="maint-success-banner" className="mb-6 rounded-xl border border-emerald-300 bg-emerald-50 p-5 flex items-start gap-3 fade-up">
          <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={26} />
          <div className="flex-1">
            <p className="font-display font-bold text-lg text-emerald-800">Your maintenance was successfully submitted</p>
            <p className="text-sm text-emerald-700 mt-0.5">One of the maintenance team will be in contact with you soon.</p>
          </div>
          <button data-testid="dismiss-success-banner" onClick={() => setJustSubmitted(false)} className="text-emerald-600 hover:text-emerald-800"><X size={18} /></button>
        </div>
      )}
      <Card className="overflow-hidden mb-6 fade-up">
        <div className="h-40 relative">
          <img src={p.cover_file_id ? fileUrl(p.cover_file_id) : p.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70" />
          <div className="absolute bottom-0 p-5 text-white">
            <h2 className="font-display text-xl font-bold">{p.address}</h2>
            <p className="text-sm text-slate-200">{p.city}, {p.postcode}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100">
          <div className="p-4"><p className="text-xs text-slate-400 uppercase tracking-wider">Monthly Rent</p><p className="font-display font-bold text-lg tabular-nums text-slate-900">{gbp(p.rent_pcm)}</p></div>
          <div className="p-4"><p className="text-xs text-slate-400 uppercase tracking-wider">Last Payment</p><p className="font-display font-bold text-lg tabular-nums text-emerald-600">{lastPayment ? gbp(lastPayment.amount) : "—"}</p></div>
          <div className="p-4"><p className="text-xs text-slate-400 uppercase tracking-wider">Balance</p><p className="font-display font-bold text-lg tabular-nums text-slate-900">£0</p></div>
        </div>
        {data.rent_due_day && (
          <div data-testid="tenant-rent-due" className="px-4 py-3 border-t border-slate-100 bg-blue-50/60 text-sm text-slate-700">
            Rent is due on the <b>{ordinal(data.rent_due_day)}</b> of each month{data.next_rent_due ? <> · next payment due <b>{fmtDate(data.next_rent_due)}</b></> : null}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900">Maintenance</h3>
            <button data-testid="report-maintenance-button" onClick={() => setOpen(true)} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors"><Plus size={15} /> Report Issue</button>
          </div>
          <div className="space-y-3">
            {data.maintenance.length === 0 && <p className="text-sm text-slate-400">No maintenance requests.</p>}
            {data.maintenance.map((m) => {
              const Icon = statusIcon[m.status] || Clock;
              return (
                <Card key={m.request_id} className="p-4 fade-up" data-testid={`tenant-maint-${m.request_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={16} className="text-blue-700" />
                    <p className="text-sm font-medium text-slate-800">{m.title}</p>
                    <span className="ml-auto"><Badge>{m.status}</Badge></span>
                  </div>
                  <p className="text-sm text-slate-500">{m.description}</p>
                  <p className="text-xs text-slate-400 mt-2">Reported {fmtDate(m.date)} · {m.priority} priority</p>
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <button data-testid={`tenant-messages-toggle-${m.request_id}`} onClick={() => setExpandedMaint(expandedMaint === m.request_id ? null : m.request_id)}
                      className="text-xs font-medium text-blue-700 hover:underline flex items-center gap-1.5">
                      <MessageSquare size={13} /> Messages{m.messages && m.messages.length ? ` (${m.messages.length})` : ""}
                      {m.messages && m.messages.length > 0 && m.messages[m.messages.length - 1].sender === "staff" && (
                        <span className="w-2 h-2 bg-red-500 rounded-full" data-testid={`tenant-newreply-${m.request_id}`} />
                      )}
                    </button>
                    {expandedMaint === m.request_id && (
                      <div className="mt-2">
                        <MaintenanceThread messages={m.messages || []} side="tenant" busy={replyBusy} onSend={(t) => tenantReply(m.request_id, t)} placeholder="Reply — e.g. when you'll be home for access…" />
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Payment History</h3>
          <Card className="overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold tracking-wider"><tr><th className="py-2.5 px-4 text-left">Date</th><th className="text-left">Description</th><th className="text-right px-4">Amount</th></tr></thead>
              <tbody>
                {data.payments.map((t) => (
                  <tr key={t.txn_id} className="border-b border-slate-100 last:border-0" data-testid={`tenant-payment-${t.txn_id}`}>
                    <td className="py-2.5 px-4 text-xs text-slate-500 font-mono">{fmtDate(t.date)}</td>
                    <td className="text-sm text-slate-700">{t.description}</td>
                    <td className="text-sm px-4 text-right tabular-nums font-medium text-emerald-600">{gbp(t.amount)}</td>
                  </tr>
                ))}
                {data.payments.length === 0 && <tr><td colSpan="3" className="text-center text-sm text-slate-400 py-6">No payments yet.</td></tr>}
              </tbody>
            </table>
          </Card>

          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Documents</h3>
          <div className="space-y-2">
            {data.documents.length === 0 && <p className="text-sm text-slate-400">No documents shared yet.</p>}
            {data.documents.map((d) => (
              <Card key={d.document_id} className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center"><FileText size={16} /></div>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{d.title}</p><p className="text-xs text-slate-400">{fmtDate(d.date)}</p></div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="font-display text-xl font-bold text-slate-900">Report Maintenance</h3><button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button></div>
            <form onSubmit={submit} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">What's the issue?</label><input data-testid="maint-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Leaking tap" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Priority</label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">{["Low", "Medium", "High", "Urgent"].map((o) => <option key={o}>{o}</option>)}</select></div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Photos / files <span className="text-xs font-normal text-slate-400">(optional)</span></label>
                <label data-testid="maint-file-label" className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 flex items-center justify-center gap-2 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors cursor-pointer">
                  <Paperclip size={15} /> {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Attach photos or a video of the issue"}
                  <input data-testid="maint-file-input" type="file" multiple accept="image/*,video/*,application/pdf" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </label>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {files.map((f, i) => (
                      <span key={i} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 flex items-center gap-1 max-w-[160px]">
                        <span className="truncate">{f.name}</span>
                        <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="submit-maintenance-button" type="submit" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Submit Report</button>
            </form>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
